import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Fab, Zoom } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useProjectConfig } from "../context/ProjectConfigContext";
import { LandingScopeProvider, useLanding } from "../context/LandingContext";
import { buildWhatsAppUrl } from "../utils/landingMedia";
import {
  applyLandingSegmentInstance,
  getHomeLandingSegmentInstances,
  isLandingSegmentInstanceVisible,
  navbarOffsetSx,
  resolveNavbarStyle,
} from "../utils/landingLayout";
import { getSegmentDefinition } from "../utils/segmentRegistry";
import { segmentCssVariables } from "../utils/segmentVisualStyle";
import { landingSectionBackgroundSx } from "../utils/landingSectionBackground";
import { resolveSiteExperienceDefaults } from "../utils/siteExperienceDefaults";
import {
  floatingWhatsappButtonSx,
  normalizeFloatingWhatsappStyle,
} from "../utils/floatingWhatsappVisualOwnership";
import { customBackgroundSx } from "../utils/customVisualOwnership";

class LandingSegmentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // Una sección legacy defectuosa no debe tirar la landing completa.
    // El error queda visible en consola para diagnóstico sin exponer datos al usuario.
    console.error("[storefront] Se omitió una sección que falló al renderizar", {
      segmentId: this.props.segmentId,
      segmentType: this.props.segmentType,
      error,
      componentStack: info?.componentStack || "",
    });
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function LandingSegmentInstance({ instance, landing, productsEnabled, gastronomicMenuEnabled, language, showCatalogAnchor, catalogAnchorOffset, sectionDomId }) {
  const scopedLanding = useMemo(
    () => resolveSiteExperienceDefaults(
      applyLandingSegmentInstance(landing, instance),
      { productsEnabled, gastronomicMenuEnabled },
    ),
    [landing, instance, productsEnabled, gastronomicMenuEnabled]
  );
  const definition = getSegmentDefinition(instance.type);
  if (!definition || !isLandingSegmentInstanceVisible(instance, scopedLanding, productsEnabled)) return null;
  const Component = definition.component;
  const sectionBackgroundSx = instance?.type === "categories"
    ? {}
    : instance?.type === "custom"
      ? customBackgroundSx(instance?.style, instance?.visualStyle)
      : landingSectionBackgroundSx(instance?.visualStyle);
  const content = (
    <Component
      language={language}
      sectionDomId={sectionDomId || instance.type}
      sectionBackgroundSx={sectionBackgroundSx}
      segmentVisualStyle={instance?.visualStyle || {}}
      segmentStyle={instance?.style || {}}
    />
  );

  return (
    <LandingScopeProvider landing={scopedLanding}>
      <Box
        data-landing-layout-id={instance.id}
        data-landing-layout-type={instance.type}
        sx={{
          ...(definition.wrapVisualStyle === false || instance.type === "custom" ? {} : segmentCssVariables(scopedLanding.theme, instance.type)),
        }}
      >
        {showCatalogAnchor ? (
          <>
            <Box id="catalog" data-landing-section="catalog" aria-hidden sx={{ height: 0, scrollMarginTop: catalogAnchorOffset }} />
            <Box id="productos" aria-hidden sx={{ height: 0, scrollMarginTop: catalogAnchorOffset }} />
          </>
        ) : null}
        {content}
      </Box>
    </LandingScopeProvider>
  );
}

export default function HomePage({ language }) {
  const { productsEnabled, gastronomicMenuEnabled } = useProjectConfig();
  const landingContext = useLanding();
  const landing = landingContext.landing || landingContext;
  const { sections, business, contact, navigation, theme, widgets } = landing;
  const floatingWhatsappStyle = normalizeFloatingWhatsappStyle(widgets?.floatingWhatsapp?.style);
  const floatingWhatsappSx = floatingWhatsappButtonSx(floatingWhatsappStyle);
  const [showFab, setShowFab] = useState(true);
  const lastScroll = useRef(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const handleScroll = () => {
      const current = window.scrollY || 0;
      setShowFab(current < lastScroll.current || current < 120);
      lastScroll.current = current;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const whatsappUrl = buildWhatsAppUrl(business?.whatsapp, contact?.whatsappMessage);
  const navbarEnabled = sections?.showNavbar !== false;
  const navbarStyle = resolveNavbarStyle(navigation, theme);
  const navbarOffset = navbarOffsetSx(navbarStyle, navbarEnabled);
  const instances = useMemo(() => getHomeLandingSegmentInstances(landing.layout, sections), [landing.layout, sections]);
  const visibleInstances = useMemo(
    () => instances
      .map((instance) => ({
        instance,
        scoped: resolveSiteExperienceDefaults(
          applyLandingSegmentInstance(landing, instance),
          { productsEnabled, gastronomicMenuEnabled },
        ),
      }))
      .filter(({ instance, scoped }) => isLandingSegmentInstanceVisible(instance, scoped, productsEnabled))
      .map(({ instance }) => instance),
    [instances, landing, productsEnabled, gastronomicMenuEnabled]
  );
  const firstVisibleIsHero = visibleInstances[0]?.type === "hero";
  const firstCatalogIndex = visibleInstances.findIndex((instance) => ["spotlight", "featured", "brands", "categories"].includes(instance.type));

  return (
    <Box sx={{ pt: firstVisibleIsHero ? 0 : navbarOffset }}>
      {visibleInstances.map((instance, index) => {
        const firstOfType = visibleInstances.findIndex((candidate) => candidate.type === instance.type) === index;
        return (
          <LandingSegmentErrorBoundary
            key={instance.id}
            segmentId={instance.id}
            segmentType={instance.type}
          >
            <LandingSegmentInstance
              instance={instance}
              landing={landing}
              productsEnabled={productsEnabled}
              gastronomicMenuEnabled={gastronomicMenuEnabled}
              language={language}
              showCatalogAnchor={index === firstCatalogIndex}
              catalogAnchorOffset={navbarOffset}
              sectionDomId={instance.anchor || (instance.type === "custom" ? instance.id : firstOfType ? instance.type : `section-${instance.id}`)}
            />
          </LandingSegmentErrorBoundary>
        );
      })}

      {whatsappUrl && sections?.showFloatingWhatsapp !== false ? (
        <Zoom in={showFab}>
          <Fab
            component="a"
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={contact?.floatingWhatsappAriaLabel ?? "Contactar por WhatsApp"}
            sx={{
              ...floatingWhatsappSx,
              position: "fixed",
              right: { xs: 18, md: 26 },
              bottom: { xs: 18, md: 26 },
              zIndex: 1300,
              transition: "background-color .15s ease, color .15s ease, border-color .15s ease, box-shadow .15s ease, transform .15s ease",
            }}
          >
            <WhatsAppIcon />
          </Fab>
        </Zoom>
      ) : null}
    </Box>
  );
}
